import React, { useMemo, useState } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, Stack, Box,
  Checkbox, FormControlLabel, Chip, TextField, Alert, LinearProgress, Accordion,
  AccordionSummary, AccordionDetails, InputAdornment,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SearchIcon from '@mui/icons-material/Search';
import {
  WEDDING_SERVICES_QUERY, ALL_SERVICE_CATEGORIES_QUERY, MY_VENDOR_CONTRACTED_SERVICE_IDS_QUERY,
  CREATE_VENDOR_SERVICE_CONTRACT_MUTATION, MY_VENDOR_READINESS_QUERY,
} from '../graphql/queries';

// Vendor picks services from the full catalog; submitting creates a contract that the
// vendor signs and the admin approves before the services go live.
// onAddDetails(category): open the form to add that service's details (a service needs them before its contract)
export default function ChooseServicesDialog({ open, onClose, onCreated, vendorUserId, onAddDetails }) {
  // An admin choosing services for a vendor acts for that vendor
  const context = vendorUserId ? { headers: { 'X-Vendor-User-Id': vendorUserId } } : undefined;
  const [selected, setSelected] = useState(new Set());
  const [notes, setNotes] = useState('');
  const [filter, setFilter] = useState('');
  const [error, setError] = useState('');

  const { data: svcData, loading } = useQuery(WEDDING_SERVICES_QUERY, { variables: { activeOnly: true }, skip: !open });
  const { data: catData } = useQuery(ALL_SERVICE_CATEGORIES_QUERY, { variables: { activeOnly: true }, skip: !open });
  const { data: takenData } = useQuery(MY_VENDOR_CONTRACTED_SERVICE_IDS_QUERY, { skip: !open, fetchPolicy: 'network-only', context });
  const [createContract, { loading: saving }] = useMutation(CREATE_VENDOR_SERVICE_CONTRACT_MUTATION, { context });
  // A service can go into a contract only when its details are complete (services/vendorReadiness.js)
  const { data: readyData } = useQuery(MY_VENDOR_READINESS_QUERY, { skip: !open, fetchPolicy: 'network-only', context });
  const readiness = readyData?.myVendorReadiness;
  const readyCats = useMemo(() => new Set((readiness?.services || []).filter(s => s.ready).map(s => s.category)), [readiness]);
  const missingOf = (category) => {
    const s = (readiness?.services || []).find(x => x.category === category);
    return s ? s.checks.filter(c => !c.done).map(c => c.label) : ['Add the details of this service on My Services'];
  };
  const blocked = (category) => !readiness || !readiness.businessReady || !readyCats.has(category);

  const taken = useMemo(() => new Set(takenData?.myVendorContractedServiceIds || []), [takenData]);
  const groups = useMemo(() => {
    const names = Object.fromEntries((catData?.allServiceCategories || []).map(c => [c.name, c.displayName]));
    const q = filter.trim().toLowerCase();
    const byCat = {};
    (svcData?.weddingServices || [])
      .filter(s => !q || s.name.toLowerCase().includes(q) || (names[s.category] || s.category).toLowerCase().includes(q))
      .forEach(s => { (byCat[s.category] = byCat[s.category] || []).push(s); });
    return Object.entries(byCat)
      .map(([category, services]) => ({ category, displayName: names[category] || category, services }))
      .sort((a, b) => a.displayName.localeCompare(b.displayName));
  }, [svcData, catData, filter]);

  const toggle = (id) => setSelected(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const toggleGroup = (services, on) => setSelected(prev => {
    const next = new Set(prev);
    services.filter(s => !taken.has(String(s.id)) && !blocked(s.category)).forEach(s => (on ? next.add(s.id) : next.delete(s.id)));
    return next;
  });

  // Top level: every service shown (after the search) that isn't already in a contract
  const allAvailable = groups.flatMap(g => g.services.filter(s => !taken.has(String(s.id)) && !blocked(g.category)));
  const allOn = allAvailable.length > 0 && allAvailable.every(s => selected.has(s.id));
  const someOn = allAvailable.some(s => selected.has(s.id));

  const reset = () => { setSelected(new Set()); setNotes(''); setFilter(''); setError(''); };
  const close = () => { reset(); onClose(); };

  const submit = async () => {
    setError('');
    try {
      const { data } = await createContract({ variables: { serviceIds: [...selected], notes: notes.trim() || null } });
      reset();
      onCreated(data.createVendorServiceContract);
    } catch (e) {
      setError(e.message);
    }
  };

  return (
    <Dialog open={open} onClose={close} maxWidth="md" fullWidth>
      <DialogTitle>Choose the services you'll offer</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" mb={2}>
          {vendorUserId
            ? "Select the services this vendor will provide. A contract is created and sent to the vendor to review and sign; after you countersign, their offerings for these services become available to families and matchmakers."
            : "Select every service you want to provide. We'll create a contract for them: you sign it, the admin approves it, and then your offerings for these services become available to bride/groom families and matchmakers."}
        </Typography>
        {readiness && !readiness.businessReady && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Finish your business details first (My Services → Get ready): {readiness.business.filter(c => !c.done).map(c => c.label).join(', ')}.
          </Alert>
        )}
        {readiness && readiness.businessReady && (
          <Alert severity="info" sx={{ mb: 2 }}>
            First add each service's details (Add service details: description, packages with prices, at least 3 photos, cancellation terms);
            a service can be chosen here once its checklist is complete. {readyCats.size ? `Ready: ${(readiness.services || []).filter(s => s.ready && !s.contracted).map(s => s.displayName).join(', ') || 'none new'}.` : 'None is complete yet.'}
          </Alert>
        )}
        <TextField
          size="small" fullWidth placeholder="Search services" value={filter} onChange={e => setFilter(e.target.value)} sx={{ mb: 2 }}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
        />
        {loading && <LinearProgress sx={{ mb: 2 }} />}
        {allAvailable.length > 0 && (
          <Box sx={{ px: 2, py: 0.5, mb: 1, borderRadius: 1, bgcolor: 'action.hover', display: 'flex', alignItems: 'center' }}>
            <FormControlLabel
              sx={{ flex: 1 }}
              control={<Checkbox checked={allOn} indeterminate={someOn && !allOn} onChange={e => toggleGroup(allAvailable, e.target.checked)} />}
              label={<Typography fontWeight={700}>{filter.trim() ? 'Select all matching services' : 'Select all services'}</Typography>}
            />
            <Typography variant="caption" color="text.secondary">
              {allAvailable.filter(s => selected.has(s.id)).length} of {allAvailable.length} · {groups.length} categor{groups.length === 1 ? 'y' : 'ies'}
            </Typography>
          </Box>
        )}
        {groups.map(g => {
          const isBlocked = blocked(g.category);
          const available = isBlocked ? [] : g.services.filter(s => !taken.has(String(s.id)));
          const chosen = g.services.filter(s => selected.has(s.id)).length;
          const allChosen = available.length > 0 && available.every(s => selected.has(s.id));
          return (
            <Accordion key={g.category} disableGutters defaultExpanded={Boolean(filter)}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ width: '100%' }}>
                  {/* Select the whole category without opening it */}
                  <Checkbox
                    size="small" checked={allChosen} indeterminate={chosen > 0 && !allChosen} disabled={!available.length}
                    onClick={e => e.stopPropagation()} onFocus={e => e.stopPropagation()}
                    onChange={e => toggleGroup(g.services, e.target.checked)}
                    inputProps={{ 'aria-label': `Select all ${g.displayName}` }}
                    sx={{ p: 0.5, ml: -0.5 }}
                  />
                  <Typography fontWeight={600} sx={{ flex: 1 }}>{g.displayName}</Typography>
                  <Typography variant="caption" color="text.secondary">{g.services.length} service{g.services.length === 1 ? '' : 's'}</Typography>
                  {chosen > 0 && <Chip size="small" color="primary" label={`${chosen} selected`} />}
                  {isBlocked && g.services.some(s => !taken.has(String(s.id))) && (
                    <Chip size="small" color="warning" variant="outlined" label="Complete details first" title={missingOf(g.category).join(' · ')} />
                  )}
                  {available.length < g.services.length && (
                    <Chip size="small" variant="outlined" label={`${g.services.length - available.length} in your contracts`} />
                  )}
                </Stack>
              </AccordionSummary>
              <AccordionDetails>
                {isBlocked && g.services.some(s => !taken.has(String(s.id))) && (
                  <Alert severity="warning" variant="outlined" sx={{ mb: 1 }}
                    action={onAddDetails && readiness?.businessReady && (
                      <Button color="inherit" size="small" onClick={() => onAddDetails(g.category)}>
                        {(readiness.services || []).find(x => x.category === g.category)?.offeringId ? 'Complete details' : 'Add details'}
                      </Button>
                    )}>
                    Still needed for {g.displayName}: {missingOf(g.category).join(' · ')}
                  </Alert>
                )}
                <Stack spacing={0.5}>
                  {g.services.map(s => {
                    const isTaken = taken.has(String(s.id));
                    return (
                      <Box key={s.id} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                        <Checkbox size="small" checked={isTaken || selected.has(s.id)} disabled={isTaken || isBlocked} onChange={() => toggle(s.id)} sx={{ mt: -0.5 }} />
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="body2" fontWeight={500}>
                            {s.name} {isTaken && <Chip size="small" label="Already in your contract" sx={{ ml: 1 }} />}
                          </Typography>
                          {s.description && <Typography variant="caption" color="text.secondary">{s.description}</Typography>}
                        </Box>
                      </Box>
                    );
                  })}
                </Stack>
              </AccordionDetails>
            </Accordion>
          );
        })}
        {!loading && !groups.length && <Alert severity="info">No services match your search.</Alert>}
        <TextField
          label="Notes for the admin (optional)" value={notes} onChange={e => setNotes(e.target.value)}
          fullWidth multiline rows={2} size="small" sx={{ mt: 2 }}
          placeholder="e.g. areas you serve, availability, anything the admin should know"
        />
        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      </DialogContent>
      <DialogActions>
        <Typography variant="body2" color="text.secondary" sx={{ flex: 1, pl: 2 }}>
          {selected.size} service{selected.size === 1 ? '' : 's'} selected
        </Typography>
        <Button onClick={close}>Cancel</Button>
        <Button variant="contained" onClick={submit} disabled={!selected.size || saving}>Create contract</Button>
      </DialogActions>
    </Dialog>
  );
}
